import React from "react";
import { Chart } from "react-google-charts";
import { Typography, Box, CircularProgress, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const PlaysPerDayChart = ({
  clicksByDay,
  loading,
  selectedQuickTaleTitle = null,
  onClearSelection,
  rangeLabel,
}) => {
  const isFiltered = Boolean(selectedQuickTaleTitle);

  // Total plays across all days in the current period.
  const totalPlays = clicksByDay
    ? Object.values(clicksByDay).reduce((sum, count) => sum + count, 0)
    : 0;

  // Summary row: date range on the left, total plays for the period.
  const renderSummary = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 4,
        mb: 1,
        flexWrap: "wrap",
      }}
    >
      {rangeLabel && (
        <Typography sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 13, color: "#666" }}>
          {rangeLabel}
        </Typography>
      )}
      <Typography sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 13, color: "#666" }}>
        Total Plays: <strong>{totalPlays}</strong>
      </Typography>
    </Box>
  );

  // Header showing the title + (when filtered) a clearable chip.
  const renderHeader = () => {
    if (!isFiltered) return null;
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 1 }}>
        <Chip
          label={`Filtered: ${selectedQuickTaleTitle}`}
          onDelete={onClearSelection}
          deleteIcon={<CloseIcon />}
          sx={{
            fontFamily: "Avenir Book, sans-serif",
            backgroundColor: "#E3F0FF",
            color: "#00a7cd",
          }}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!clicksByDay || Object.keys(clicksByDay).length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        {renderSummary()}
        {renderHeader()}
        <Typography
          variant="h6"
          sx={{ fontFamily: "Avenir Book, sans-serif", color: "#333", mb: 2 }}
        >
          {isFiltered
            ? `Plays per Day — ${selectedQuickTaleTitle}`
            : "Total Plays per Day"}
        </Typography>
        <Typography sx={{ fontFamily: "Avenir Book, sans-serif", color: "#999" }}>
          No play data for this period.
        </Typography>
      </Box>
    );
  }

  // Sort days chronologically
  const sortedDays = Object.keys(clicksByDay).sort();

  // Format dates for display
  const data = [
    ["Date", "Plays"],
    ...sortedDays.map((dateKey) => {
      const [year, month, day] = dateKey.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return [formattedDate, clicksByDay[dateKey]];
    }),
  ];

  const options = {
    title: isFiltered
      ? `Plays per Day — ${selectedQuickTaleTitle}`
      : "Total Plays per Day",
    titleTextStyle: {
      color: "#333",
      fontSize: 16,
      bold: true,
      fontFamily: "Avenir Book, sans-serif",
    },
    fontFamily: "Avenir Book, sans-serif",
    colors: ["#00a7cd"],
    legend: { position: "none" },
    bar: { groupWidth: "60%" },
    vAxis: {
      minValue: 0,
      format: "0",
      textStyle: { fontFamily: "Avenir Book, sans-serif", fontSize: 12 },
    },
    hAxis: {
      textStyle: {
        fontFamily: "Avenir Book, sans-serif",
        fontSize: 10,
      },
      slantedText: true,
      slantedTextAngle: 45,
    },
    chartArea: { width: "85%", height: "65%" },
    animation: { startup: true, duration: 500, easing: "out" },
  };

  return (
    <Box>
      {renderSummary()}
      {renderHeader()}
      <Chart
        chartType="ColumnChart"
        width="100%"
        height="400px"
        data={data}
        options={options}
      />
    </Box>
  );
};

export default PlaysPerDayChart;
